import { useState } from 'react';
import { ChevronRight, ShieldAlert, Check, X, Loader2 } from 'lucide-react';
import type { ToolCallRecord } from '@cortex/shared';
import { Api } from '../../lib/api';
import { useUi } from '../../store/ui';

const RISK_CLASS: Record<string, string> = { SAFE: 'ok', LOW: '', MEDIUM: 'warn', HIGH: 'crit' };

export function ToolCard({ call, onDecide }: { call: ToolCallRecord; onDecide?: (approve: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const status = call.status;
  const light = status === 'OK' ? 'ok' : status === 'RUNNING' ? 'teal' : status === 'AWAITING_APPROVAL' ? 'warn' : status === 'DENIED' || status === 'FAILED' ? 'crit' : '';
  return (
    <div className={`tool-card ${status.toLowerCase()}`}>
      <button className="tool-head" onClick={() => setOpen(!open)}>
        <ChevronRight size={12} className={`chev${open ? ' open' : ''}`} />
        {status === 'RUNNING' ? <Loader2 size={12} className="spin" /> : <span className={`light ${light}`} />}
        <span className="mono name">{call.tool}</span>
        <span className="summary">{call.summary ?? (status === 'AWAITING_APPROVAL' ? 'needs your approval' : status === 'FAILED' || status === 'DENIED' ? call.error : status === 'RUNNING' ? 'running…' : '')}</span>
        {call.durationMs != null && <span className="mono dur">{call.durationMs} ms</span>}
        <span className={`chip ${RISK_CLASS[call.riskLevel] ?? ''}`} style={{ fontSize: 9 }}>{call.riskLevel}</span>
      </button>
      {status === 'AWAITING_APPROVAL' && onDecide && (
        <div className="tool-approve">
          <ShieldAlert size={14} />
          <span>
            The agent wants to run <b>{call.tool}</b>
            {inputTarget(call) ? <> against <b className="mono">{inputTarget(call)}</b></> : null}
            {typeof call.input.command === 'string' ? <> · command <code className="mono">{String(call.input.command)}</code></> : null}
            {call.credential ? <> · credential <b className="mono">{call.credential}</b></> : call.tool.startsWith('net.ssh') ? <> · <span style={{ color: 'var(--down)' }}>no matching credential</span></> : null}. Nothing runs until you say so.
          </span>
          <button className="primary" onClick={() => onDecide(true)}><Check size={12} /> Approve</button>
          <button className="danger" onClick={() => onDecide(false)}><X size={12} /> Deny</button>
        </div>
      )}
      {open && (
        <div className="tool-body">
          {call.credential && <div className="faint mono" style={{ fontSize: 10, marginBottom: 4 }}>credential: {call.credential}</div>}
          <div className="label">Input</div>
          <pre className="mono">{JSON.stringify(call.input, null, 1)}</pre>
          {call.output !== null && call.output !== undefined && (
            <>
              <div className="label">Output</div>
              <pre className="mono">{typeof call.output === 'string' ? call.output : JSON.stringify(call.output, null, 1)}</pre>
            </>
          )}
          {call.error && <div className="tool-err mono">{call.error}</div>}
          {call.approvedBy && <div className="faint mono" style={{ fontSize: 10 }}>approved by {call.approvedBy}</div>}
          {status === 'OK' && /^(net\.|onyx\.|starlink\.)/.test(call.tool) && !call.tool.startsWith('net.ssh') && <WatchButton call={call} />}
        </div>
      )}
    </div>
  );
}

/** One click turns a diagnostic into a watcher: same tool, same input, a rule on one field. */
function WatchButton({ call }: { call: ToolCallRecord }) {
  const toast = useUi((s) => s.toast);
  const create = () => {
    const field = window.prompt('Field to watch (dot path into the output), e.g. results.0.value, received, open, data.status', defaultField(call));
    if (!field) return;
    const ruleText = window.prompt('Rule: "lt -70", "gt 5", "eq false", "changed", or "delta 6" (vs 24-reading median)', 'changed');
    if (!ruleText) return;
    const [kind, val] = ruleText.trim().split(/\s+/);
    const rule = kind === 'delta' ? { field, type: 'delta' as const, deltaAbs: Number(val || 1), window: 24 } : { field, type: 'threshold' as const, op: (kind as 'gt' | 'lt' | 'gte' | 'lte' | 'eq' | 'ne' | 'changed') || 'changed', ...(val !== undefined ? { value: val === 'true' ? true : val === 'false' ? false : Number.isNaN(Number(val)) ? val : Number(val) } : {}) };
    const cron = window.prompt('Cadence (cron, Brisbane time)', '0 * * * *') || '0 * * * *';
    void Api.watchers.create({ name: `${call.tool} ${inputTarget(call) ?? ''} ${field}`.trim(), fromCallId: call.id, rule, cron, enabled: true, tool: call.tool, input: call.input })
      .then((w) => toast(`Watcher "${w.name}" created (${w.cron})`))
      .catch((e) => toast((e as Error).message, 'crit'));
  };
  return <button className="ghost" style={{ marginTop: 6, fontSize: 11 }} onClick={create}>Watch this</button>;
}

function defaultField(call: ToolCallRecord): string {
  if (call.tool === 'net.ping') return 'received';
  if (call.tool === 'net.tcp_check') return 'open';
  if (call.tool.startsWith('net.snmp')) return 'results.0.value';
  if (call.tool === 'net.http_probe') return 'status';
  return 'available';
}

function inputTarget(call: ToolCallRecord): string | null {
  const i = call.input;
  for (const k of ['target', 'host', 'url', 'device', 'ref', 'cidr']) if (typeof i[k] === 'string') return i[k] as string;
  return null;
}
